import { useEffect, useMemo, useRef } from "react";
import { useInput } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Typography, Alert, Paper, Chip, Divider } from "@mui/material";
import TemplateSelector from "./TemplateSelector";
import ParameterMapper from "./ParameterMapper";
import BroadcastPreview from "./BroadcastPreview";
import { useTemplateContext } from "@/containers/whatsapp-broadcasts/context";
import { useTemplateValidation } from "./hooks";
import type { TemplateBodyRecord } from "./hooks";
import type { BroadcastParameter } from "./utils";
import {
  DEFAULT_EVENT_RSVP_TEMPLATE_NAME,
  DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME,
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME,
  DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME,
} from "./utils";
import { formatSabilOutstandingTypeLabel } from "./sabil-outstanding-type-label";

type TemplateSelectionStepProps = {
  source?: string;
  onValidationChange?: (isValid: boolean, errorMessage: string | null) => void;
};

const TemplateSelectionStep = ({
  source = "_templateStep",
  onValidationChange,
  ...props
}: TemplateSelectionStepProps) => {
  const { field } = useInput({ source, ...props });
  const { control } = useFormContext();

  const templateName = useWatch({ control, name: "templateName" });
  const broadcastKind = useWatch({ control, name: "broadcastKind" }) as string | undefined;
  const sabilType = useWatch({ control, name: "sabilType" }) as string | undefined;
  const templateValidationDisabled =
    broadcastKind === "MIQAAT_RSVP" ||
    broadcastKind === "EVENT_RSVP" ||
    broadcastKind === "FMB_OUTSTANDING" ||
    broadcastKind === "SABIL_OUTSTANDING" ||
    broadcastKind === "MUTTAVATTEEN_OUTSTANDING";
  const isFmbOutstanding = broadcastKind === "FMB_OUTSTANDING";
  const isSabilOutstanding = broadcastKind === "SABIL_OUTSTANDING";
  const isMuttavatteenOutstanding = broadcastKind === "MUTTAVATTEEN_OUTSTANDING";
  // Set precisely per tab (Chula/Establishment or Monthly/Yearly) by BroadcastModeFields.
  const lockedTemplateName = isFmbOutstanding
    ? DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME
    : isSabilOutstanding
      ? templateName || DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME
      : isMuttavatteenOutstanding
        ? templateName || DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME
        : DEFAULT_EVENT_RSVP_TEMPLATE_NAME;

  const { data: selectedTemplate, isLoading: isLoadingTemplate } = useTemplateContext();

  const watchedParameters = useWatch({
    control,
    name: "parameters",
    defaultValue: {},
  });

  const csvColumnHeaders = useWatch({
    control,
    name: "csvColumnHeaders",
    defaultValue: [],
  });

  const parametersRef = useRef({ value: {} as Record<string, unknown>, serialized: "" });
  const currentSerialized = JSON.stringify(watchedParameters || {});

  if (parametersRef.current.serialized !== currentSerialized) {
    parametersRef.current = {
      value: (watchedParameters || {}) as Record<string, unknown>,
      serialized: currentSerialized,
    };
  }

  const parameters = parametersRef.current.value;

  const { isValid, errorMessage, expectedVariables } = useTemplateValidation(
    templateName,
    selectedTemplate as TemplateBodyRecord | undefined,
    isLoadingTemplate,
    parameters as Record<string, BroadcastParameter> | undefined,
    Array.isArray(csvColumnHeaders) ? csvColumnHeaders : [],
    { disabled: templateValidationDisabled }
  );

  const validationState = useMemo(
    () => ({
      isValid,
      errorMessage,
    }),
    [isValid, errorMessage]
  );

  const lastStateSerializedRef = useRef<string | null>(null);

  const currentStateSerialized = useMemo(
    () =>
      JSON.stringify({
        templateName,
        parameters: parametersRef.current.value,
        isValid: validationState.isValid,
        expectedVariablesCount: expectedVariables?.length || 0,
      }),
    [templateName, validationState.isValid, expectedVariables?.length]
  );

  useEffect(() => {
    if (lastStateSerializedRef.current !== currentStateSerialized) {
      lastStateSerializedRef.current = currentStateSerialized;

      const stateToStore = {
        templateName,
        parameters: parametersRef.current.value,
        isValid: validationState.isValid,
        expectedVariablesCount: expectedVariables?.length || 0,
      };

      field.onChange(stateToStore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStateSerialized]);

  const prevValidationRef = useRef<{ isValid: boolean | null; errorMessage: string | null }>({
    isValid: null,
    errorMessage: null,
  });
  useEffect(() => {
    if (onValidationChange) {
      if (
        prevValidationRef.current.isValid !== validationState.isValid ||
        prevValidationRef.current.errorMessage !== validationState.errorMessage
      ) {
        prevValidationRef.current = {
          isValid: validationState.isValid,
          errorMessage: validationState.errorMessage,
        };
        onValidationChange(validationState.isValid, validationState.errorMessage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationState.isValid, validationState.errorMessage]);

  const automatedVariables = useMemo(() => {
    if (isSabilOutstanding) {
      if (sabilType === "ESTABLISHMENT") {
        return [
          { index: "{{1}}", label: "Establishment Name", source: "Ledger" },
          { index: "{{2}}", label: "ITS / Sabil ID", source: "Directory" },
          { index: "{{3}}", label: "As-of Date", source: "System" },
          { index: "{{4}}", label: "Total Due", source: "Ledger" },
          { index: "{{5}}", label: "Total Paid", source: "Ledger" },
          { index: "{{6}}", label: "Outstanding Balance", source: "Ledger" },
        ];
      }
      return [
        { index: "{{1}}", label: "Full Name", source: "Directory" },
        { index: "{{2}}", label: "ITS ID", source: "Directory" },
        { index: "{{3}}", label: "As-of Date", source: "System" },
        { index: "{{4}}", label: "Monthly Rate", source: "Ledger" },
        { index: "{{5}}", label: "Total Due", source: "Ledger" },
        { index: "{{6}}", label: "Total Paid", source: "Ledger" },
        { index: "{{7}}", label: "Outstanding Balance", source: "Ledger" },
      ];
    }
    if (isMuttavatteenOutstanding) {
      return [
        { index: "{{1}}", label: "Full Name", source: "Directory" },
        { index: "{{2}}", label: "ITS ID", source: "Directory" },
        { index: "{{3}}", label: "As-of Date", source: "System" },
        { index: "{{4}}", label: "Total Due", source: "Ledger" },
        { index: "{{5}}", label: "Total Paid", source: "Ledger" },
        { index: "{{6}}", label: "Outstanding Balance", source: "Ledger" },
      ];
    }
    if (isFmbOutstanding) {
      return [
        { index: "{{1}}", label: "ITS ID", source: "Directory" },
        { index: "{{2}}", label: "Household Name", source: "Directory" },
        { index: "{{3}}", label: "Takhmeen Amount", source: "Ledger" },
        { index: "{{4}}", label: "Paid Amount", source: "Ledger" },
        { index: "{{5}}", label: "Outstanding Balance", source: "Ledger" },
      ];
    }
    const isMiqaatRsvp = broadcastKind === "MIQAAT_RSVP";
    const isEventRsvp = broadcastKind === "EVENT_RSVP";
    if (isMiqaatRsvp || isEventRsvp) {
      return [
        { index: "{{1}}", label: "Recipient Name", source: "Directory" },
        {
          index: "{{2}}",
          label: isMiqaatRsvp ? "Miqaat Name" : "Event Name",
          source: "System",
        },
        { index: "{{3}}", label: "Venue / Markaz", source: "System" },
        { index: "{{4}}", label: "Slot Type & Date", source: "System" },
      ];
    }
    return [
      { index: "{{1}}", label: "Recipient Name", source: "Directory" },
      { index: "{{2}}", label: "Event / Miqaat", source: "System" },
      { index: "{{3}}", label: "Venue", source: "System" },
      { index: "{{4}}", label: "Date & Time", source: "System" },
    ];
  }, [isSabilOutstanding, sabilType, isMuttavatteenOutstanding, isFmbOutstanding, broadcastKind]);

  if (templateValidationDisabled) {
    const isMiqaatRsvp = broadcastKind === "MIQAAT_RSVP";
    const modeLabel = isSabilOutstanding
      ? `Sabil • ${formatSabilOutstandingTypeLabel(sabilType)}`
      : isMuttavatteenOutstanding
        ? `Mutawatteen • ${formatSabilOutstandingTypeLabel(sabilType)}`
        : isFmbOutstanding
          ? "FMB Outstanding"
          : isMiqaatRsvp
            ? "Miqaat RSVP"
            : "Event RSVP";

    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          minWidth: 0,
        }}
      >
        {/* Left Column: Configuration & Parameter Mappings */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "0 0 58%", lg: "0 0 60%" },
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "background.paper",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {modeLabel}
                </Typography>
                <Chip
                  label={lockedTemplateName}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace", fontSize: "0.72rem", fontWeight: 600 }}
                />
              </Box>
              <Chip
                label="Auto-configured"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 500 }}
              />
            </Box>

            <Divider />

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Automated Variable Bindings
                </Typography>
                <Chip
                  label={`${automatedVariables.length} variables`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                {automatedVariables.map((variable) => (
                  <Box
                    key={variable.index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1,
                      px: 1.25,
                      borderRadius: 1.5,
                      bgcolor: "action.hover",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Chip
                        label={variable.index}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                          fontFamily: "monospace",
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 500 }}>
                        {variable.label}
                      </Typography>
                    </Box>
                    <Chip
                      label={variable.source}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.68rem", color: "text.secondary" }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Right Column: Live Message Preview */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "0 0 38%", lg: "0 0 36%" },
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <BroadcastPreview />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "0 0 58%", lg: "0 0 60%" },
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {templateName &&
          !isLoadingTemplate &&
          expectedVariables &&
          expectedVariables.length > 0 &&
          validationState.isValid && (
            <Alert severity="success" sx={{ mb: 2 }}>
              All required parameters are filled.
            </Alert>
          )}

        <TemplateSelector />

        <Divider sx={{ my: 1.5 }} />

        {templateName && <ParameterMapper />}
      </Box>

      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "0 0 38%", lg: "0 0 36%" },
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <BroadcastPreview />
      </Box>
    </Box>
  );
};

export default TemplateSelectionStep;
